import { useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { useConnection } from 'wagmi';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import {
  BridgeToken,
  DEFAULT_BRIDGE_INPUT_DECIMALS,
} from 'structure/bridge/constants/bridgeTokens';
import {
  firstDecimals,
  getSupportedTokens,
} from 'structure/bridge/utils/bridgeStateHelpers';
import { DECIMALS_INTERNAL } from 'structure/bridge/constants/constants';
import { Input } from 'baseui/input';
import { OptionProfile, StatefulMenu } from 'baseui/menu';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { LabelSmall } from 'baseui/typography';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import styles from '../bridge.module.css';
import Image from 'next/image';
import { getStaticAssetUrl } from 'utils/url';
export function BridgeAmountInput({ chainId }: { chainId: number }) {
  const { t } = useTranslation('common');
  const { address: userAddress } = useConnection();
  const [amount, setAmount] = useState<number>(0);
  const [debouncedAmount] = useDebounce(amount, 666);
  const [bridgeState, setBridgeState] = useAtom(bridgeAtom);

  // in order to only show tokens that exist on the current chain
  // we first filter the tokens and then set the default selected currency
  // filter out tokens that are not deployed on current chain
  const filteredTokens: BridgeToken[] = useMemo(
    () => getSupportedTokens(chainId),
    [chainId]
  );

  // set default selected token
  const [selectedCurrency, setSelectedCurrency] = useState(filteredTokens[0]);

  // reset amount and token on changes
  useEffect(() => {
    const prevState = bridgeState;
    setBridgeState({
      ...prevState,
      token: filteredTokens[0],
      userBalance: BigInt(0),
      amount: 0,
      nativeFee: BigInt(0),
      tokenFee: BigInt(0),
      tokenQuoteLoading: false,
    });
    setSelectedCurrency(filteredTokens[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId, userAddress]);

  // set bridge state to user input with debounce to avoid ddos'ing rpc
  useEffect(() => {
    const prevState = bridgeState;
    setBridgeState({
      ...prevState,
      token: selectedCurrency,
      amount: isNaN(debouncedAmount)
        ? 0
        : firstDecimals(debouncedAmount, DECIMALS_INTERNAL),
      nativeFee: BigInt(0),
      tokenFee: BigInt(0),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAmount]);

  // check bridge state if user sets max value or bridge max in other components
  // and set amount to show in UI
  useEffect(() => {
    const number: number = firstDecimals(
      bridgeState.amount,
      DEFAULT_BRIDGE_INPUT_DECIMALS
    );
    setAmount(number);
  }, [bridgeState.amount]);

  // set amount on user input
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    const checkedValueFormatted: number = firstDecimals(
      value,
      DEFAULT_BRIDGE_INPUT_DECIMALS
    );
    setAmount(checkedValueFormatted > 0 ? checkedValueFormatted : 0);
  };

  const onClickMax = () => {
    const value = parseFloat(
      (
        bridgeState.userBalance / BigInt(10 ** bridgeState.token?.decimals)
      ).toString()
    );
    const checkedValueFormatted: number = firstDecimals(
      value,
      DEFAULT_BRIDGE_INPUT_DECIMALS
    );
    setAmount(checkedValueFormatted > 0 ? checkedValueFormatted : 0);
  };

  return (
    <div className='mt-8 rounded-none pb-2'>
      <LabelSmall
        className={cx(twMerge(styles.label, 'mb-2 text-white-600'))}
        font={'14px'}
      >
        {t('AMOUNT')}
      </LabelSmall>
      <Input
        onChange={handleAmountChange}
        value={amount > 0 ? amount : 0}
        overrides={{
          StartEnhancer: {
            style: (_) => {
              return {
                backgroundColor: 'transparent',
                minWidth: '10rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                paddingLeft: '0',
                paddingRight: '0',
                paddingTop: '16px',
                paddingBottom: '16px',
              };
            },
          },
          Input: {
            style: (_) => {
              return {
                backgroundColor: '#000000',
                fontFamily: 'Montserrat',
                textAlign: 'right',
              };
            },
          },
          Root: {
            style: (_) => {
              return {
                backgroundColor: '#000000',
                fontFamily: 'Montserrat',
                borderTopLeftRadius: '0px',
                borderTopRightRadius: '0px',
                borderBottomLeftRadius: '0px',
                borderBottomRightRadius: '0px',
                borderLeftWidth: '0.25px',
                borderRightWidth: '0.25px',
                borderTopWidth: '0.25px',
                borderBottomWidth: '0.25px',
                borderLeftStyle: 'solid',
                borderBottomStyle: 'solid',
                borderRightStyle: 'solid',
                borderTopStyle: 'solid',
                borderTopLeftColor: '#B5B5B5',
                borderBottomLeftColor: '#B5B5B5',
                borderTopRightColor: '#B5B5B5',
                borderBottomRightColor: '#B5B5B5',
              };
            },
          },
        }}
        startEnhancer={
          <StatefulPopover
            focusLock
            placement={PLACEMENT.bottomLeft}
            content={({ close }) => (
              <StatefulMenu
                items={filteredTokens}
                onItemSelect={({ item }) => {
                  const prevState = bridgeState;
                  setBridgeState({
                    ...prevState,
                    token: item,
                    amount: 0, // reset amount on token change
                    nativeFee: BigInt(0),
                    tokenFee: BigInt(0),
                    userBalance: BigInt(0),
                    tokenQuoteLoading: false,
                  });
                  setSelectedCurrency(item);
                  close();
                }}
                overrides={{
                  List: {
                    style: {
                      maxHeight: '220px',
                      height: '100%',
                      minWidth: '171px',
                      backgroundColor: 'white',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTopLeftRadius: '0px',
                      borderTopRightRadius: '0px',
                      borderBottomLeftRadius: '0px',
                      borderBottomRightRadius: '0px',
                      border: '1px solid #B5B5B5 !important',
                      background: '#000000',
                      color: 'var(--style-20-ffffff-light-text, #FFF)',
                    },
                  },
                  Option: {
                    component: OptionProfile,
                    props: {
                      getProfileItemLabels: ({
                        symbol,
                      }: {
                        symbol: string;
                      }) => ({
                        subtitle: symbol,
                      }),
                      getProfileItemImg: (item: BridgeToken) => item.imageUrl,
                      getProfileItemImgText: (item: BridgeToken) => item.symbol,
                      overrides: {
                        ListItemProfile: {
                          style: {
                            paddingTop: '0px',
                            paddingBottom: '0px',
                            paddingLeft: '0px',
                            paddingRight: '0px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'start',
                          },
                        },
                        ProfileImg: {
                          style: {
                            width: '32px',
                            height: '32px',
                          },
                        },
                        ProfileLabelsContainer: {
                          style: {
                            marginTop: '24px',
                            marginBottom: '24px',
                            marginRight: '24px',
                            marginLeft: '-8px',
                          },
                        },
                        ProfileSubtitle: {
                          style: {
                            'font-size': '14px',
                            'font-family': 'Montserrat !important',
                            'font-style': 'normal',
                            'font-weight': 500,
                            'line-height': '22px',
                          },
                        },
                      },
                    },
                  },
                }}
              />
            )}
          >
            <div className='flex cursor-pointer flex-row items-center gap-2'>
              <Image
                src={selectedCurrency.imageUrl ?? ''}
                className='rounded-full'
                alt='TokenIcon'
                width={24}
                height={24}
              />
              <LabelSmall
                className={cx(twMerge(styles.bridgeFont, 'text-lg uppercase'))}
              >
                {selectedCurrency.symbol}
              </LabelSmall>
              <Image
                src={getStaticAssetUrl('/sfi/static/svgs/arrow-down.svg')}
                width={16}
                height={16}
                alt='arrow-down'
              />
            </div>
          </StatefulPopover>
        }
        placeholder={`${t('AMOUNT')} ${selectedCurrency}`}
        clearOnEscape
      />
      <div className='flex justify-end'>
        <button
          onClick={onClickMax}
          className='mt-1 text-sm font-medium text-white-400'
        >
          {t('MAX')}
        </button>
      </div>
    </div>
  );
}
